import { useEffect } from 'react';
import { useAppDispatch } from 'store/hooks';
import { useAuth } from 'react-oidc-context';

import './app.scss';
import 'styles/index.scss';

// components
import SplashRoot from 'components/Splash/SplashRoot';
import SplashScreen from 'components/Splash/SplashScreen';
import Navbar from 'components/Navbar/Navbar';
import AppRoutes from 'components/Routes/Routes';
import LoginPage from 'components/LoginPage/LoginPage';
import CookieBanner from 'components/CookieBanner/CookieBanner';
// models
import { InitSteps } from 'models';
// store
import { setProfileKeycloak } from 'store/profileKeycloakUserSlice/profileKeycloakUserSlice.slice';
// hooks
import useFluidProvidersCallback from 'hooks/useFluidProvidersCallback';
import { useMatomoTracker } from 'hooks/useMatomoTracker';
import useUserInfosUpdater from 'hooks/useUserInfosUpdater';
import useTargetNavigationRedirect from 'hooks/useTargetNavigationRedirect';
import usePageTitle from 'hooks/usePageTitle';

export const App = () => {
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const providersRedirectUrl = useFluidProvidersCallback();
  const targetNavRedirectUrl = useTargetNavigationRedirect();
  const redirectUri = providersRedirectUrl ?? targetNavRedirectUrl;
  useMatomoTracker();
  useUserInfosUpdater();
  usePageTitle();

  useEffect(() => {
    return auth.events.addAccessTokenExpiring(() => {
      auth.signinSilent();
    });
  }, [auth.events, auth.signinSilent]);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      dispatch(setProfileKeycloak(auth.user));
    }
  }, [auth.isAuthenticated]);

  useEffect(() => {
    if (auth.isAuthenticated && redirectUri) {
      auth.signinRedirect({ redirect_uri: redirectUri });
    }
  }, [auth.isAuthenticated, redirectUri]);

  if (!auth.isAuthenticated || auth.error) {
    return <LoginPage redirectUri={redirectUri} />;
  }

  // When redirectUri is set, we're about to redirect via Keycloak
  // Show a simple loading screen instead of SplashRoot to avoid
  // API errors flashing during the consent callback redirect
  if (redirectUri) {
    return (
      <div className="splash-root">
        <SplashScreen initStep={InitSteps.CONSOS} />
      </div>
    );
  }

  return (
    <SplashRoot>
      <Navbar />
      <CookieBanner />
      <main className="app-content">
        <AppRoutes />
      </main>
    </SplashRoot>
  );
};

export default App;
